import { Matomo } from '2-feature/Matomo/Matomo';
import { useSiteContext } from '3-foundation/Site/SiteContext';
import { usePageContext } from '3-foundation/PageContent/PageContext';
import Head from 'next/head';
import { useNavigationContext } from '3-foundation/Navigation/NavigationContext';
import { getSearchSiteId } from '3-foundation/Search/getSearchSiteId';
import { useRouter } from 'next/router';

export const HeadRegion = ({ title, description, noFollow, noIndex, tags }: Content.Page['properties']) => {
	const siteContext = useSiteContext();
	const pageContext = usePageContext();
	const { locale } = useRouter();
	const openGraph = pageContext?.getSettings('openGraph');
	const { image, imageText, description: ogDescription, title: ogTitle } = openGraph || {};
	const { title: siteTitle, root, favicons } = siteContext ?? {};
	const pageTitle = pageContext.getSettings('navigation')?.navigationTitle ?? pageContext.name;
	const navContext = useNavigationContext();
	const breadcrumbItems = navContext?.getBreadcrumb({ currentUrl: pageContext.path, includeRootItem: false });
	return (
		<>
			<Head>
				{/* Title and meta must be direct children of <Head>
				 * https://nextjs.org/docs/pages/api-reference/components/head#use-minimal-nesting
				 */}
				<title>{`${title ? title : pageContext.name}${siteTitle ? ' - ' + siteTitle : ''}`}</title>
				<meta name="description" content={description} />

				<meta name="robots" content={`${noFollow ? 'nofollow' : 'follow'}, ${noIndex ? 'noindex' : 'index'}`} />

				<meta property="og:url" content={pageContext.path} key="og:url" />
				<meta property="og:type" content="website" key="og:type" />
				<meta property="og:title" content={ogTitle ? ogTitle : pageTitle} key="og:title" />
				<meta property="og:description" content={ogDescription ? ogDescription : description} key="og:description" />
				{image && <meta property="og:image" content={image.url} key="og:image" />}
				{image && <meta name="og:image:alt" content={imageText} key="og:image:alt" />}
				{siteTitle && <meta property="og:site_name" content={siteTitle} />}
				<meta name="twitter:card" content="summary_large_image" key="twitter:card" />

				{/* Search Meta */}
				<meta name="search-site" content={getSearchSiteId(root?.id)} />
				<meta name="search-title" content={pageTitle} key="search-title" />
				{description && <meta name="search-description" content={description} />}
				<meta name="search-page-type" content={pageContext?.documentType} />
				<meta name="search-locale" content={locale} />
				{pageContext?.parentId && <meta name="search-parent-id" content={pageContext?.parentId} />}
				{tags?.subjectTags && <meta name="search-tags-subject" content={tags.subjectTags?.join('|')} />}
				{tags?.statusTags && <meta name="search-tags-status" content={tags.statusTags?.join('|')} />}
				{tags?.typeTags && <meta name="search-tags-type" content={tags.typeTags?.join('|')} />}
				{breadcrumbItems && <meta name="search-breadcrumb" content={breadcrumbItems.map((item) => item.name).join(' › ')} />}
				{favicons?.faviconAsSvg && <link rel="icon" type="image/svg+xml" href={favicons.faviconAsSvg} />}
			</Head>
			{siteContext?.settings?.matomo && <Matomo {...siteContext?.settings?.matomo} />}
		</>
	);
};
